import type { PublicSiteNavigation, Theme } from '@parkingbase/ecommerce-api-dev';

import {
  isHttpsUrl,
  isRecord,
  resolveSiteLink,
  resolveSitePagePath,
} from '@/app/(public)/site-page-mappers';
import { isSiteActionVisible } from '@/components/site-page/useSiteActions';
import {
  SITE_ACTION_NAMES,
  SITE_ICON_NAMES,
  type SiteAction,
  type SiteAudience,
  type SiteIcon,
  type SiteLinkView,
  type SiteNavIconView,
  type SiteNavKind,
  type SiteNavNodeView,
  type SiteNavZone,
  type SiteNavZonesView,
} from '@/components/site-page/SitePage.types';
import { CITATION_ROUTES, MY_ACCOUNT_ROUTES, PUBLIC_ROUTES } from '@/routes/routes.constants';

/** The deepest level a navigation may reach: top-level item, group child, flyout item. */
export const MAX_NAV_DEPTH = 3;

const NAV_KINDS: readonly string[] = ['GROUP', 'PAGE', 'LINK'];
const NAV_ZONES: readonly string[] = ['LEFT', 'CENTER', 'RIGHT'];
const NAV_AUDIENCES: readonly string[] = ['ANY', 'SIGNED_IN', 'SIGNED_OUT'];
const ICON_NAMES: ReadonlySet<string> = new Set(SITE_ICON_NAMES);
const ACTION_NAMES: ReadonlySet<string> = new Set(SITE_ACTION_NAMES);
/** The LINK target types a navigation item may carry besides ACTION. */
const LINK_TARGET_TYPES: readonly string[] = ['URL', 'ROUTE', 'WHITE_LABEL_TAB'];

/** What an item does, before the viewer is known. */
export type SiteNavTargetModel =
  { type: 'link'; link: SiteLinkView } | { type: 'action'; action: SiteAction };

/** One validated item of the published navigation (audience not yet applied). */
export interface SiteNavNodeModel {
  id: string;
  kind: SiteNavKind;
  label?: string;
  a11yLabel: string;
  icon: SiteNavIconView | null;
  audience: SiteAudience;
  /** Top-level items only. */
  align?: SiteNavZone;
  /** PAGE and LINK items. Null for a GROUP. */
  target: SiteNavTargetModel | null;
  /** GROUP items: the Overview destination, when the api resolved one. */
  overview: SiteLinkView | null;
  children: SiteNavNodeModel[];
}

export interface SiteNavigationModel {
  /** `PUBLISHED` only when the api said exactly that; every other answer is `DEFAULT`. */
  source: 'PUBLISHED' | 'DEFAULT';
  showLogin: boolean;
  nodes: SiteNavNodeModel[];
  /** Raw FOOTER slots for `mapFooterSlots`; null when the api sent none. */
  footerSlots: Record<string, unknown> | null;
  /** Items the api sent at any level that failed validation and were dropped. */
  droppedNodes: number;
}

export const DEFAULT_SITE_NAVIGATION: SiteNavigationModel = {
  source: 'DEFAULT',
  showLogin: true,
  nodes: [],
  footerSlots: null,
  droppedNodes: 0,
};

function textOf(value: unknown): string | undefined {
  if (typeof value !== 'string') return undefined;
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : undefined;
}

function mapIcon(raw: unknown): SiteNavIconView | null {
  if (!isRecord(raw)) return null;
  if (raw.source === 'LIBRARY') {
    return typeof raw.value === 'string' && ICON_NAMES.has(raw.value)
      ? { source: 'LIBRARY', name: raw.value as SiteIcon }
      : null;
  }
  if (raw.source === 'UPLOAD') {
    if (typeof raw.url !== 'string' || !isHttpsUrl(raw.url)) return null;
    const icon: SiteNavIconView = { source: 'UPLOAD', url: raw.url };
    if (typeof raw.width === 'number' && raw.width > 0) icon.width = raw.width;
    if (typeof raw.height === 'number' && raw.height > 0) icon.height = raw.height;
    return icon;
  }
  return null;
}

function mapTarget(kind: SiteNavKind, raw: unknown): SiteNavTargetModel | null {
  if (!isRecord(raw)) return null;
  if (kind === 'PAGE') {
    const link = resolveSitePagePath(raw.path);
    return link ? { type: 'link', link } : null;
  }
  const { type, value } = raw;
  if (type === 'ACTION') {
    return typeof value === 'string' && ACTION_NAMES.has(value) && value !== 'URL'
      ? { type: 'action', action: value as SiteAction }
      : null;
  }
  if (typeof type !== 'string' || !LINK_TARGET_TYPES.includes(type)) return null;
  const link = resolveSiteLink({ type, value });
  return link ? { type: 'link', link } : null;
}

/**
 * Validates one api node. Returns null for anything that would render as a dead or unsafe item:
 * the caller drops it and keeps its siblings.
 */
/** Counts the items validation dropped, at every level, for one payload. */
interface DropTally {
  dropped: number;
}

function mapNode(
  raw: unknown,
  depth: number,
  index: number,
  parentId: string,
  tally: DropTally
): SiteNavNodeModel | null {
  if (!isRecord(raw)) return null;
  const kind = raw.kind;
  if (typeof kind !== 'string' || !NAV_KINDS.includes(kind)) return null;
  // A GROUP at the deepest level would need a fourth level that the header does not draw.
  if (kind === 'GROUP' && depth >= MAX_NAV_DEPTH) return null;

  let align: SiteNavZone | undefined;
  if (depth === 1) {
    if (typeof raw.align !== 'string' || !NAV_ZONES.includes(raw.align)) return null;
    align = raw.align as SiteNavZone;
  }

  const audienceRaw = raw.audience ?? 'ANY';
  if (typeof audienceRaw !== 'string' || !NAV_AUDIENCES.includes(audienceRaw)) return null;

  const label = textOf(raw.label);
  const icon = mapIcon(raw.icon);
  if (!label && !icon) return null;
  // An item with no accessible name at all cannot be announced, so it is not shown.
  const a11yLabel = textOf(raw.a11yLabel) ?? label;
  if (!a11yLabel) return null;

  // The fallback carries the parent, so two id-less items at the same depth never share an id.
  const id = textOf(raw.id) ?? `${parentId}-${index}`;
  const base = {
    id,
    kind: kind as SiteNavKind,
    label,
    a11yLabel,
    icon,
    audience: audienceRaw as SiteAudience,
    align,
  };

  if (kind === 'GROUP') {
    const children = mapNodes(raw.children, depth + 1, id, tally);
    if (children.length === 0) return null;
    return { ...base, target: null, overview: resolveSitePagePath(raw.overviewPath), children };
  }

  const target = mapTarget(kind as SiteNavKind, raw.target);
  // A PAGE or LINK whose target does not resolve would be a dead item.
  if (!target) return null;
  return { ...base, target, overview: null, children: [] };
}

function mapNodes(
  raw: unknown,
  depth: number,
  parentId: string,
  tally: DropTally
): SiteNavNodeModel[] {
  if (!Array.isArray(raw)) return [];
  const nodes: SiteNavNodeModel[] = [];
  raw.forEach((item, index) => {
    // Dropping one malformed item is validation, not a failure: its siblings are kept.
    const node = mapNode(item, depth, index, parentId, tally);
    if (node) nodes.push(node);
    else tally.dropped += 1;
  });
  return nodes;
}

/**
 * Maps the generated `PublicSiteNavigation` to the validated model.
 *
 * ## Invariants:
 * - THROWS on a payload that is not a navigation at all (not an object, a `nodes` that is not an
 *   array, a missing or unrecognised `source`). The store logs that as `siteNavigation.loadFailed`
 *   and shows today's header: a malformed answer is a failed read, never a quiet DEFAULT.
 * - `source` is `PUBLISHED` only when the api says exactly that. `DEFAULT` and `PREVIEW` (never sent
 *   by this endpoint) are DEFAULT.
 * - Dropping an individual malformed node is validation: no throw, siblings kept. Every drop is
 *   counted in `droppedNodes` so the store can surface a broken publish.
 * - An item with no `id` gets one derived from its parent's id and its index, unique in the tree.
 * - Every string is carried as text. Links pass through `resolveSiteLink` / `resolveSitePagePath`
 *   (so `javascript:` and `data:` never become an href); an uploaded icon is kept only for an
 *   https URL. Nothing here produces markup.
 */
export function mapSiteNavigation(raw: PublicSiteNavigation | unknown): SiteNavigationModel {
  if (!isRecord(raw)) throw new Error('site navigation payload is not an object');
  const source = raw.source;
  if (source !== 'PUBLISHED' && source !== 'DEFAULT' && source !== 'PREVIEW') {
    throw new Error('site navigation payload has no recognised source');
  }
  if (source !== 'PUBLISHED') return DEFAULT_SITE_NAVIGATION;
  if (!Array.isArray(raw.nodes)) throw new Error('site navigation payload has no nodes array');
  const header = isRecord(raw.header) ? raw.header : null;
  const tally: DropTally = { dropped: 0 };
  return {
    source: 'PUBLISHED',
    showLogin: header?.showLogin !== false,
    nodes: mapNodes(raw.nodes, 1, 'nav', tally),
    footerSlots: isRecord(raw.footer) ? raw.footer : null,
    droppedNodes: tally.dropped,
  };
}

/** What the viewer-dependent resolution needs. */
export interface SiteNavResolveContext {
  isSignedIn: boolean;
  theme: Theme | null | undefined;
  validationVisible: boolean;
  loginHref: string;
  /** Opens the find-session pop-up; null where the layout provides none. */
  openFindSession: (() => void) | null;
}

/** The destination of an ACTION item: a link, a pop-up button, or nothing (the item is dropped). */
export type SiteActionResolution =
  { kind: 'link'; link: SiteLinkView } | { kind: 'button'; onActivate: () => void } | null;

const internal = (href: string): SiteActionResolution => ({
  kind: 'link',
  link: { href, external: false },
});

/**
 * The destination of a LINK item of type ACTION, matching `useSiteActions.run`'s non-homepage
 * branches, or null when the action is hidden for this company/viewer.
 *
 * ## Invariants:
 * - Visibility is `isSiteActionVisible`, the rule `useSiteActions` uses, so a tile and a header
 *   item with the same action never disagree.
 * - PAY_BY_ZONE: signed out goes to sign-in; signed in opens the find-session pop-up where the
 *   layout provides it and is dropped where it does not.
 */
export function siteActionLink(
  action: SiteAction,
  ctx: SiteNavResolveContext
): SiteActionResolution {
  if (!isSiteActionVisible(action, ctx)) return null;
  switch (action) {
    case 'BOOK':
      return internal(MY_ACCOUNT_ROUTES.reserve);
    case 'MONTHLY':
      return internal(`${MY_ACCOUNT_ROUTES.reserve}?mode=monthly`);
    case 'PAY_CITATION':
      return internal(CITATION_ROUTES.lookup);
    case 'VALIDATE':
      return internal(MY_ACCOUNT_ROUTES.parkingValidation);
    case 'MY_ACCOUNT':
      return internal(ctx.isSignedIn ? MY_ACCOUNT_ROUTES.overview : ctx.loginHref);
    case 'CONTACT_SALES':
      return internal(PUBLIC_ROUTES.contact);
    case 'PAY_BY_ZONE':
      if (!ctx.isSignedIn) return internal(ctx.loginHref);
      return ctx.openFindSession ? { kind: 'button', onActivate: ctx.openFindSession } : null;
    case 'URL':
      return null;
  }
}

function audienceAllows(audience: SiteAudience, isSignedIn: boolean): boolean {
  if (audience === 'SIGNED_IN') return isSignedIn;
  if (audience === 'SIGNED_OUT') return !isSignedIn;
  return true;
}

function resolveNode(node: SiteNavNodeModel, ctx: SiteNavResolveContext): SiteNavNodeView | null {
  if (!audienceAllows(node.audience, ctx.isSignedIn)) return null;
  const shared = {
    id: node.id,
    kind: node.kind,
    label: node.label,
    a11yLabel: node.a11yLabel,
    icon: node.icon,
  };
  if (node.kind === 'GROUP') {
    const children = resolveNodes(node.children, ctx);
    // A group emptied by audience or hidden actions is dropped, at every level.
    if (children.length === 0) return null;
    return { ...shared, link: null, overview: node.overview, children };
  }
  if (!node.target) return null;
  if (node.target.type === 'link') {
    return { ...shared, link: node.target.link, overview: null, children: [] };
  }
  const resolved = siteActionLink(node.target.action, ctx);
  if (!resolved) return null;
  if (resolved.kind === 'button') {
    return {
      ...shared,
      link: null,
      onActivate: resolved.onActivate,
      overview: null,
      children: [],
    };
  }
  return { ...shared, link: resolved.link, overview: null, children: [] };
}

function resolveNodes(nodes: SiteNavNodeModel[], ctx: SiteNavResolveContext): SiteNavNodeView[] {
  const views: SiteNavNodeView[] = [];
  for (const node of nodes) {
    const view = resolveNode(node, ctx);
    if (view) views.push(view);
  }
  return views;
}

/**
 * Applies the viewer (audience, actions) to the validated navigation and splits the top level into
 * its three zones, in authored order.
 *
 * ## Invariants:
 * - Audience is presentation only; it gates no data (every target keeps its own guard).
 * - A GROUP whose children are all filtered out is dropped, recursively, so a level-2 group left
 *   empty disappears too.
 */
export function buildSiteNavZones(
  nodes: SiteNavNodeModel[],
  ctx: SiteNavResolveContext
): SiteNavZonesView {
  const zones: SiteNavZonesView = { left: [], center: [], right: [] };
  for (const node of nodes) {
    const view = resolveNode(node, ctx);
    if (!view) continue;
    if (node.align === 'LEFT') zones.left.push(view);
    else if (node.align === 'CENTER') zones.center.push(view);
    else if (node.align === 'RIGHT') zones.right.push(view);
  }
  return zones;
}
